import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Button, TextField, Stack, Alert, Grid, Divider,
  InputAdornment, IconButton, ToggleButtonGroup, ToggleButton, Chip,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import api from '../services/api';

const Section = ({ title, subtitle, children }) => (
  <Paper sx={{ p: 3 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
    {subtitle && <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{subtitle}</Typography>}
    <Box sx={{ mt: subtitle ? 0 : 1.5 }}>{children}</Box>
  </Paper>
);

const Preference = ({ mode, setMode }) => {
  const [user, setUser] = useState(null);
  const [pw, setPw] = useState({ current: '', next: '', confirm: '' });
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null); // { type, text }

  useEffect(() => {
    api.get('/users/me').then((r) => setUser(r.data)).catch(() => {});
  }, []);

  const onPw = (e) => setPw({ ...pw, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setMsg(null);
    if (pw.next.length < 8) return setMsg({ type: 'error', text: 'New password must be at least 8 characters.' });
    if (pw.next !== pw.confirm) return setMsg({ type: 'error', text: 'New password and confirmation do not match.' });
    if (pw.next === pw.current) return setMsg({ type: 'error', text: 'New password must be different from the current one.' });
    setSaving(true);
    try {
      await api.post('/users/change-password', { currentPassword: pw.current, newPassword: pw.next });
      setMsg({ type: 'success', text: 'Password changed successfully.' });
      setPw({ current: '', next: '', confirm: '' });
    } catch (err) {
      setMsg({ type: 'error', text: err?.response?.data?.message || 'Failed to change password.' });
    } finally {
      setSaving(false);
    }
  };

  const pwField = (name, label, autoComplete) => (
    <TextField
      name={name} label={label} value={pw[name]} onChange={onPw}
      type={show ? 'text' : 'password'} fullWidth required autoComplete={autoComplete}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton size="small" onClick={() => setShow((s) => !s)} edge="end">
              {show ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );

  return (
    <Box sx={{ maxWidth: 760 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Section title="Account">
            {user ? (
              <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
                <Box><Typography variant="caption" color="text.secondary">Username</Typography><Typography>{user.username || '—'}</Typography></Box>
                <Box><Typography variant="caption" color="text.secondary">Email</Typography><Typography>{user.email || '—'}</Typography></Box>
                <Box><Typography variant="caption" color="text.secondary">Role</Typography><Box><Chip size="small" label={user.role} sx={{ textTransform: 'capitalize' }} /></Box></Box>
              </Stack>
            ) : <Typography variant="body2" color="text.secondary">Loading…</Typography>}
          </Section>
        </Grid>

        <Grid item xs={12} md={7}>
          <Section title="Change password" subtitle="Use at least 8 characters. You'll stay signed in on this device.">
            <form onSubmit={submit}>
              <Stack spacing={2}>
                {msg && <Alert severity={msg.type} onClose={() => setMsg(null)}>{msg.text}</Alert>}
                {pwField('current', 'Current password', 'current-password')}
                <Divider flexItem />
                {pwField('next', 'New password', 'new-password')}
                {pwField('confirm', 'Confirm new password', 'new-password')}
                <Box>
                  <Button type="submit" variant="contained" startIcon={<LockOutlinedIcon />} disabled={saving}>
                    {saving ? 'Updating…' : 'Update password'}
                  </Button>
                </Box>
              </Stack>
            </form>
          </Section>
        </Grid>

        <Grid item xs={12} md={5}>
          <Section title="Appearance" subtitle="Choose your interface theme.">
            <ToggleButtonGroup exclusive value={mode} onChange={(_, v) => v && setMode(v)} size="small">
              <ToggleButton value="light"><LightModeOutlinedIcon fontSize="small" sx={{ mr: 1 }} /> Light</ToggleButton>
              <ToggleButton value="dark"><DarkModeOutlinedIcon fontSize="small" sx={{ mr: 1 }} /> Dark</ToggleButton>
            </ToggleButtonGroup>
          </Section>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Preference;
